/**
 * Automations condition-field catalog (BAL-8506 / BAL-8361 Wave 0).
 *
 * The SINGLE SOURCE the FE condition builder (field dropdown = intersection over the selected report
 * types) and the BE create-validator (reject any `var` not available on EVERY selected type) both
 * derive from. Each field declares its `report.*` fact var, the JsonLogic `shape` the grammar compiles
 * it to (see `workforce-condition-grammar`), the value source the UI picker draws from, and the report
 * types it is meaningful on.
 *
 * Availability matrix is verified against the producers (BAL-8506 slice D producer contract test):
 *   - MCC / risk level / risk type / geography — all 4 report types (POS populates
 *     `contentAnalysis.mcc.primary`; merchant country + the risk model are universal).
 *   - Policy fit — underwriting scans only (the two non-ONGOING types).
 *   - Content findings / social-media presence / policy-page presence — DF only (POS produces no
 *     `riskIndicators`; social media stays DF-only per the ticket matrix even though POS populates the
 *     jsonb — see the plan-doc ledger for the pending product call to extend it).
 *
 * `report.isAlert` is trigger-owned (the Monitoring-alert card prepends it — see the grammar's
 * alert-clause helpers) and is deliberately NOT a menu field, so its presence uniquely re-derives the
 * trigger on parse.
 */

// Type-only import — importing the runtime `MERCHANT_REPORT_TYPES_MAP` here would create a
// circular module-init hazard (index.ts re-exports this file before it defines that map), so the 4
// report-type literals are inlined below and pinned to the type via `satisfies`.
import type { MerchantReportType } from './index';

/** The two live automation triggers (Resolution / API-call / Schedule are grayed "Contact us"). */
export const WORKFORCE_AUTOMATION_TRIGGERS = ['report_published', 'monitoring_alert'] as const;
export type WorkforceAutomationTrigger = (typeof WORKFORCE_AUTOMATION_TRIGGERS)[number];

/**
 * How a field's selected values compile to JsonLogic (implemented in `workforce-condition-grammar`):
 *   - `scalar-in`        — `{"in":[{"var":factVar},[…values]]}`
 *   - `array-some`       — `{"some":[{"var":factVar},{"in":[{"var":""},[…values]]}]}`
 *   - `risk-type-or`     — `{"or":[{"==":[{"var":"report.riskType.<t>.alert"},true]},…]}`
 *   - `presence-boolean` — `{"==":[{"var":factVar},true|false]}`
 */
export const WORKFORCE_CONDITION_SHAPES = [
  'scalar-in',
  'array-some',
  'risk-type-or',
  'presence-boolean',
] as const;
export type WorkforceConditionShape = (typeof WORKFORCE_CONDITION_SHAPES)[number];

/** Where the UI value picker sources its options — informational for the FE, not used by the grammar. */
export type WorkforceConditionValueSource =
  | 'risk-level'
  | 'risk-type'
  | 'mcc'
  | 'country'
  | 'policy-fit'
  | 'content-findings'
  | 'presence';

export type WorkforceConditionFieldDef = {
  label: string;
  /**
   * The `report.*` fact var this field reads. For `risk-type-or` this is the object base
   * (`report.riskType`); the grammar expands each selected value to `report.riskType.<value>.alert`.
   */
  factVar: string;
  shape: WorkforceConditionShape;
  valueSource: WorkforceConditionValueSource;
  reportTypes: readonly MerchantReportType[];
};

const ALL_AUTOMATION_REPORT_TYPES = [
  'DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
  'POINT_OF_SALE_REPORT',
  'ONGOING_POINT_OF_SALE_REPORT',
] as const satisfies readonly MerchantReportType[];

const DF_REPORT_TYPES = [
  'DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
] as const satisfies readonly MerchantReportType[];

const UNDERWRITING_REPORT_TYPES = [
  'DIGITAL_FOOTPRINT_REPORT',
  'POINT_OF_SALE_REPORT',
] as const satisfies readonly MerchantReportType[];

/** The 4-way report-type split the Automations trigger step offers. */
export const WORKFORCE_AUTOMATION_REPORT_TYPES = ALL_AUTOMATION_REPORT_TYPES;

/** Which of the 4 types the Monitoring-alert trigger constrains the selection to (the ONGOING ones). */
export const WORKFORCE_MONITORING_REPORT_TYPES = [
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_POINT_OF_SALE_REPORT',
] as const satisfies readonly MerchantReportType[];

export const WORKFORCE_CONDITION_FIELDS = {
  riskLevel: {
    label: 'Risk level',
    factVar: 'report.riskLevel',
    shape: 'scalar-in',
    valueSource: 'risk-level',
    reportTypes: ALL_AUTOMATION_REPORT_TYPES,
  },
  riskType: {
    label: 'Risk type',
    factVar: 'report.riskType',
    shape: 'risk-type-or',
    valueSource: 'risk-type',
    reportTypes: ALL_AUTOMATION_REPORT_TYPES,
  },
  policyFitStatus: {
    label: 'Policy fit',
    factVar: 'report.policyFitStatus',
    shape: 'scalar-in',
    valueSource: 'policy-fit',
    reportTypes: UNDERWRITING_REPORT_TYPES,
  },
  contentFindings: {
    label: 'Content findings',
    factVar: 'report.contentFindings',
    shape: 'array-some',
    valueSource: 'content-findings',
    reportTypes: DF_REPORT_TYPES,
  },
  mcc: {
    label: 'MCC',
    factVar: 'report.mcc',
    shape: 'scalar-in',
    valueSource: 'mcc',
    reportTypes: ALL_AUTOMATION_REPORT_TYPES,
  },
  merchantCountry: {
    label: 'Geography',
    factVar: 'report.merchantCountry',
    shape: 'scalar-in',
    valueSource: 'country',
    reportTypes: ALL_AUTOMATION_REPORT_TYPES,
  },
  socialMedia: {
    label: 'Social media',
    factVar: 'report.socialMedia.anyMissing',
    shape: 'presence-boolean',
    valueSource: 'presence',
    reportTypes: DF_REPORT_TYPES,
  },
  compliancePages: {
    label: 'Policy pages',
    factVar: 'report.compliancePages.anyMissing',
    shape: 'presence-boolean',
    valueSource: 'presence',
    reportTypes: DF_REPORT_TYPES,
  },
} as const satisfies Record<string, WorkforceConditionFieldDef>;

export type WorkforceConditionFieldKey = keyof typeof WORKFORCE_CONDITION_FIELDS;

export const WORKFORCE_CONDITION_FIELD_KEYS = Object.keys(
  WORKFORCE_CONDITION_FIELDS,
) as WorkforceConditionFieldKey[];

/** The `report.riskType` per-type alert path a `risk-type-or` value expands to. */
export const riskTypeAlertVar = (riskType: string): string => `report.riskType.${riskType}.alert`;

/**
 * The fields offered when `types` are all selected — the INTERSECTION (available on EVERY type).
 * Empty `types` yields every field (a vacuous `.every()`), which the FE gates behind "pick a type".
 */
export const conditionFieldsForReportTypes = (
  types: readonly MerchantReportType[],
): WorkforceConditionFieldKey[] =>
  WORKFORCE_CONDITION_FIELD_KEYS.filter(key =>
    types.every(type =>
      (WORKFORCE_CONDITION_FIELDS[key].reportTypes as readonly MerchantReportType[]).includes(type),
    ),
  );
